import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Avatar } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/api/auth', '/api/ai-agents')
  : 'http://localhost:5000/api/ai-agents';

export default function AgentChat() {
  const { id } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState({ name: 'AI Assistant', greeting: 'Hi! How can I help?', avatar: '', themeColor: '#6366f1', suggestedQuestions: [] });
  const [visitorName, setVisitorName] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const chatRef = useRef(null);
  const sessionId = useRef('v_' + id + '_' + Math.random().toString(36).slice(2)).current;

  useEffect(() => {
    axios.get(`${API}/${id}/info`).then(r => setInfo({ ...info, ...r.data })).catch(() => {});
  }, [id]);

  // Poll for owner replies every 5 seconds
  useEffect(() => {
    if (!chatStarted) return;
    const interval = setInterval(() => {
      axios.get(`${API}/${id}/messages/${sessionId}`).then(r => {
        const ownerMsgs = (r.data.messages || []).filter(m => m.sender === 'owner');
        const currentOwnerCount = messages.filter(m => m.role === 'owner').length;
        if (ownerMsgs.length > currentOwnerCount) {
          // First owner message — show connected notification
          if (currentOwnerCount === 0) {
            setMessages(prev => [...prev, { role: 'system', text: '✅ A human agent has joined the chat!' }]);
          }
          const newOnes = ownerMsgs.slice(currentOwnerCount);
          setMessages(prev => [...prev, ...newOnes.map(m => ({ role: 'owner', text: m.text }))]);
        }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [id, messages]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (msg) => {
    const text = msg || input.trim();
    if (!text || loading) return;
    setInput('');
    const newMessages = [...messages, { role: 'user', text }];
    setMessages(newMessages);

    // Auto-detect live agent request keywords
    const liveKeywords = /live agent|human agent|real person|talk to human|speak to someone|connect me|real agent/i;
    if (liveKeywords.test(text)) {
      const history = newMessages.map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));
      axios.post(`${API}/${id}/request-human`, { history, sessionId }).catch(() => {});
      setMessages(prev => [...prev, { role: 'bot', text: '🙋 A human agent has been notified. Someone will connect with you shortly!' }]);
      return;
    }

    // End conversation keywords
    const endKeywords = /^(no thanks|no|that's all|bye|thank you|thanks|ok bye|nothing else|nahi|theek hai)$/i;
    if (endKeywords.test(text) && messages.some(m => m.text?.includes('🎉'))) {
      setMessages(prev => [...prev, { role: 'bot', text: '👋 Thank you for chatting with us! Our team will follow up soon. Have a great day!' }]);
      axios.post(`${API}/${id}/chat`, { message: '[CONVERSATION_ENDED]', sessionId, visitorName }).catch(() => {});
      return;
    }

    setLoading(true);
    try {
      const history = newMessages.map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));
      const res = await axios.post(`${API}/${id}/chat`, { message: text, sessionId, history, visitorName });
      // Add natural delay so user sees typing indicator
      await new Promise(r => setTimeout(r, 1200));
      if (res.data.reply) {
        setMessages(prev => [...prev, { role: 'bot', text: res.data.reply }]);
      }
      if (res.data.humanTriggered) {
        setTimeout(() => {
          setMessages(prev => [...prev, { role: 'bot', text: '🙋 I\'ve notified our team. A human agent will connect with you shortly!' }]);
        }, 500);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Something went wrong. Please try again.' }]);
    } finally { setLoading(false); }
  };

  const color = info.themeColor || '#6366f1';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${color}, ${color}cc)`, px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {info.avatar ? (
          <Avatar src={info.avatar} sx={{ width: 40, height: 40, border: '2px solid rgba(255,255,255,0.3)' }} />
        ) : (
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SmartToyIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
        )}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>{info.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#4ade80' }} />
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Online</Typography>
          </Box>
        </Box>
      </Box>

      {/* Chat */}
      <Box ref={chatRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {!chatStarted ? (
          /* Pre-chat: Ask name */
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SmartToyIcon sx={{ fontSize: 28, color }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{info.name}</Typography>
            <Typography sx={{ fontSize: 13, color: '#6b7280', textAlign: 'center', maxWidth: 250 }}>{info.greeting}</Typography>
            <Box sx={{ width: '100%', maxWidth: 280 }}>
              <TextField fullWidth size="small" value={visitorName} onChange={e => setVisitorName(e.target.value)}
                placeholder="Enter your name to start..."
                onKeyDown={e => { if (e.key === 'Enter' && visitorName.trim()) setChatStarted(true); }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused fieldset': { borderColor: color } } }} />
              <Button fullWidth variant="contained" disabled={!visitorName.trim()}
                onClick={() => setChatStarted(true)}
                sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.9)' } }}>
                Start Chat
              </Button>
            </Box>
          </Box>
        ) : (
          <>
        {/* Greeting */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <SmartToyIcon sx={{ fontSize: 14, color }} />
          </Box>
          <Box sx={{ px: 2, py: 1.2, bgcolor: '#fff', borderRadius: '4px 16px 16px 16px', border: '1px solid #e5e7eb', maxWidth: '80%' }}>
            <Typography sx={{ fontSize: 14 }}>Hi {visitorName}! {info.greeting}</Typography>
          </Box>
        </Box>

        {/* Suggested Questions */}
        {messages.length === 0 && info.suggestedQuestions?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2, pl: 4.5 }}>
            {info.suggestedQuestions.map((q, i) => (
              <Box key={i} onClick={() => send(q)}
                sx={{ px: 1.5, py: 0.7, borderRadius: 2, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  border: `1px solid ${color}40`, color, bgcolor: `${color}08`,
                  transition: 'all 0.15s', '&:hover': { bgcolor: `${color}15`, borderColor: color } }}>
                {q}
              </Box>
            ))}
          </Box>
        )}

        {/* Messages */}
        {messages.map((m, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : m.role === 'system' ? 'center' : 'flex-start', mb: 1.5, gap: 1 }}>
            {m.role === 'system' ? (
              <Box sx={{ px: 2, py: 0.8, bgcolor: '#ecfdf5', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>{m.text}</Typography>
              </Box>
            ) : (
            <>
            {m.role !== 'user' && (
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: m.role === 'owner' ? '#05966920' : `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                {m.role === 'owner' ? <SupportAgentIcon sx={{ fontSize: 14, color: '#059669' }} /> : <SmartToyIcon sx={{ fontSize: 14, color }} />}
              </Box>
            )}
            <Box sx={{ px: 2, py: 1.2, maxWidth: '75%',
              borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
              bgcolor: m.role === 'user' ? color : m.role === 'owner' ? '#ecfdf5' : '#fff',
              color: m.role === 'user' ? '#fff' : '#374151',
              border: m.role === 'user' ? 'none' : m.role === 'owner' ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
              boxShadow: m.role === 'user' ? `0 2px 8px ${color}30` : 'none' }}>
              {m.role === 'owner' && <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#059669', mb: 0.3 }}>Human Agent</Typography>}
              <Typography sx={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
            </Box>
            </>
            )}
          </Box>
        ))}

        {/* Typing indicator */}
        {loading && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            {info.avatar ? (
              <img src={info.avatar} alt="bot" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <SmartToyIcon sx={{ fontSize: 14, color }} />
              </Box>
            )}
            <Box sx={{ px: 2, py: 1.2, bgcolor: '#fff', borderRadius: '4px 16px 16px 16px', border: '1px solid #e5e7eb', display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {[0,1,2].map(i => <Box key={i} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color,
                animation: `bounce 1.4s infinite ${i*0.2}s`,
                '@keyframes bounce': { '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.4 }, '40%': { transform: 'scale(1)', opacity: 1 } } }} />)}
            </Box>
          </Box>
        )}

          </>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField fullWidth size="small" value={input} onChange={e => setInput(e.target.value)}
            placeholder="Type a message..." onKeyDown={e => e.key === 'Enter' && send()}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, '&.Mui-focused fieldset': { borderColor: color } } }} />
          <Button variant="contained" onClick={() => send()} disabled={!input.trim() || loading}
            sx={{ borderRadius: 3, minWidth: 44, bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.9)' } }}>
            <SendIcon fontSize="small" />
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box />
          <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>
            Powered by <span style={{ color: '#6366f1', fontWeight: 600 }}>Talktokam</span>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
